import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/firebaseInit';
import { eventBridgeService, EVENT_TYPES } from './eventService';
import { getLinkTypeDefaults, LINK_TYPES } from '../config/entityTypeRegistry';
import {
  buildDedupKey,
  buildParticipantIds,
  canonicalize,
  validateEndpoint,
  isActiveLink
} from '../utils/linkCanonicalUtils';
import { linkRegistryConfigService } from './linkRegistryConfigService';
import { legacyLinkAdapter } from './legacyLinkAdapter';
import { shouldWriteLegacy } from '../utils/linkRegistryModeUtils';
import { normalizeRegistryEntityType } from '../config/entityTypeRegistry';

function linksCollectionRef(userId, tripId) {
  return collection(db, 'users', userId, 'trips', tripId, 'links');
}

function linkDocRef(userId, tripId, linkId) {
  return doc(db, 'users', userId, 'trips', tripId, 'links', linkId);
}

function omitUndefined(fields) {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined)
  );
}

function buildLinkDocument(linkId, endpointA, endpointB, options = {}) {
  const [entityLow, entityHigh] = canonicalize(endpointA, endpointB);
  const linkType = options.linkType;
  const defaults = getLinkTypeDefaults(linkType);
  const now = new Date().toISOString();

  return {
    linkId,
    schemaVersion: 1,
    entityLow,
    entityHigh,
    participantIds: buildParticipantIds(entityLow, entityHigh),
    linkType,
    dedupKey: buildDedupKey(endpointA, endpointB, linkType),
    linkLifecycle: options.linkLifecycle || 'active',
    linkIntent: options.linkIntent || defaults.linkIntent,
    displayMode: options.displayMode || defaults.displayMode,
    displayContext: options.displayContext ?? null,
    displayTemplate: options.displayTemplate ?? null,
    sortOrder: options.sortOrder ?? 0,
    metadata: omitUndefined({
      createdBy: options.metadata?.createdBy || 'user',
      ...options.metadata
    }),
    createdAt: options.createdAt || now,
    updatedAt: options.updatedAt || now
  };
}

async function findLinkByDedupKey(userId, tripId, dedupKey) {
  const linksRef = linksCollectionRef(userId, tripId);
  const snapshot = await getDocs(query(linksRef, where('dedupKey', '==', dedupKey)));
  if (snapshot.empty) {
    return null;
  }
  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Repairs participantIds on older link rows so getLinksForEntity(array-contains) can find them.
 */
async function ensureParticipantIds(userId, tripId, linkId, linkDoc) {
  const entityLow = linkDoc?.entityLow;
  const entityHigh = linkDoc?.entityHigh;
  if (!entityLow?.id || !entityHigh?.id) {
    return;
  }

  const expected = buildParticipantIds(entityLow, entityHigh);
  const current = Array.isArray(linkDoc.participantIds) ? [...linkDoc.participantIds].sort() : null;
  const alreadyCorrect =
    current &&
    current.length === expected.length &&
    current.every((id, index) => id === expected[index]);

  if (!alreadyCorrect) {
    await updateDoc(linkDocRef(userId, tripId, linkId), {
      participantIds: expected,
      updatedAt: new Date().toISOString()
    });
  }
}

function applyLinkFilters(links, filters = {}) {
  let result = links;
  if (filters.linkLifecycle) {
    result = result.filter((link) => link.linkLifecycle === filters.linkLifecycle);
  } else if (filters.includeArchived !== true) {
    result = result.filter((link) => isActiveLink(link));
  }
  if (filters.linkType) {
    result = result.filter((link) => link.linkType === filters.linkType);
  }
  if (filters.linkIntent) {
    result = result.filter((link) => link.linkIntent === filters.linkIntent);
  }
  return result;
}

export const connectionRegistryService = {
  /**
   * @param {import('../types/tripLink.types').LinkEndpoint} endpointA
   * @param {import('../types/tripLink.types').LinkEndpoint} endpointB
   * @param {Object} [options]
   */
  async createLink(userId, tripId, endpointA, endpointB, options = {}) {
    validateEndpoint(endpointA);
    validateEndpoint(endpointB);

    const linkType = options.linkType;
    if (!linkType) {
      throw new Error('linkType is required');
    }

    const dedupKey = buildDedupKey(endpointA, endpointB, linkType);
    const existing = await findLinkByDedupKey(userId, tripId, dedupKey);
    if (existing) {
      const existingLinkId = existing.linkId || existing.id;
      if (isActiveLink(existing)) {
        await ensureParticipantIds(userId, tripId, existingLinkId, existing);
        if (!options.skipLegacyWrite) {
          const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
          if (shouldWriteLegacy(config)) {
            await legacyLinkAdapter.writeLegacyLink(userId, tripId, existing);
          }
        }
        return { linkId: existingLinkId, created: false, deduplicated: true };
      }

      // Replace-all saves archive links; remove stale row so we can write a fresh active link.
      await deleteDoc(linkDocRef(userId, tripId, existingLinkId));
    }

    const linkId = doc(linksCollectionRef(userId, tripId)).id;
    const linkDoc = buildLinkDocument(linkId, endpointA, endpointB, { ...options, linkType });
    await setDoc(linkDocRef(userId, tripId, linkId), linkDoc);

    if (!options.skipLegacyWrite) {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (shouldWriteLegacy(config)) {
        await legacyLinkAdapter.writeLegacyLink(userId, tripId, linkDoc);
      }
    }

    eventBridgeService.publish(EVENT_TYPES.LINK_CREATED, {
      userId,
      tripId,
      linkId,
      linkType: linkDoc.linkType,
      entityLow: linkDoc.entityLow,
      entityHigh: linkDoc.entityHigh,
      linkIntent: linkDoc.linkIntent,
      displayMode: linkDoc.displayMode,
      timestamp: linkDoc.updatedAt
    });

    return { linkId, created: true, deduplicated: false };
  },

  async updateLink(userId, tripId, linkId, partialUpdate) {
    const ref = linkDocRef(userId, tripId, linkId);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) {
      throw new Error(`Link ${linkId} not found`);
    }

    const allowedFields = [
      'linkLifecycle',
      'linkIntent',
      'displayMode',
      'displayContext',
      'displayTemplate',
      'sortOrder',
      'metadata'
    ];
    const changes = {};
    allowedFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(partialUpdate, field)) {
        changes[field] = partialUpdate[field];
      }
    });

    const updatedAt = new Date().toISOString();
    await updateDoc(ref, { ...changes, updatedAt });

    eventBridgeService.publish(EVENT_TYPES.LINK_UPDATED, {
      userId,
      tripId,
      linkId,
      changes,
      timestamp: updatedAt
    });

    return { linkId, updatedAt, changes };
  },

  async removeLink(userId, tripId, linkId, { hardDelete = false } = {}) {
    const ref = linkDocRef(userId, tripId, linkId);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) {
      return { linkId, removed: false };
    }

    const linkDoc = snapshot.data();
    const timestamp = new Date().toISOString();

    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    if (shouldWriteLegacy(config)) {
      await legacyLinkAdapter.removeLegacyLink(userId, tripId, linkDoc);
    }

    if (hardDelete) {
      await deleteDoc(ref);
    } else {
      await updateDoc(ref, { linkLifecycle: 'archived', updatedAt: timestamp });
    }

    eventBridgeService.publish(EVENT_TYPES.LINK_REMOVED, {
      userId,
      tripId,
      linkId,
      entityLow: linkDoc.entityLow,
      entityHigh: linkDoc.entityHigh,
      hardDelete,
      timestamp
    });

    return { linkId, removed: true, hardDelete };
  },

  async getLinkById(userId, tripId, linkId) {
    const snapshot = await getDoc(linkDocRef(userId, tripId, linkId));
    if (!snapshot.exists()) {
      return null;
    }
    return { id: snapshot.id, ...snapshot.data() };
  },

  async getLinksForEntity(userId, tripId, _entityType, entityId, filters = {}) {
    const linksRef = linksCollectionRef(userId, tripId);
    const snapshot = await getDocs(query(
      linksRef,
      where('participantIds', 'array-contains', entityId)
    ));

    const links = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    return applyLinkFilters(links, filters);
  },

  async getLinksBetween(userId, tripId, endpointA, endpointB, linkType) {
    validateEndpoint(endpointA);
    validateEndpoint(endpointB);
    const dedupKey = buildDedupKey(endpointA, endpointB, linkType);
    const link = await findLinkByDedupKey(userId, tripId, dedupKey);
    if (!link || !isActiveLink(link)) {
      return [];
    }
    return [link];
  },

  async linkExists(userId, tripId, endpointA, endpointB, linkType) {
    const normalizedA = {
      type: normalizeRegistryEntityType(endpointA.type),
      id: endpointA.id
    };
    const normalizedB = {
      type: normalizeRegistryEntityType(endpointB.type),
      id: endpointB.id
    };
    const links = await this.getLinksBetween(userId, tripId, normalizedA, normalizedB, linkType);
    return links.length > 0;
  },

  async getAllLinks(userId, tripId, filters = {}) {
    const snapshot = await getDocs(linksCollectionRef(userId, tripId));
    const links = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    return applyLinkFilters(links, filters);
  },

  /**
   * Build a display index from the registry for surfaces that historically read legacy
   * fields (accommodation.placeReference, document.connections). One read; lookup by id.
   * Lets list/detail views show the right data on registry_only trips where those legacy
   * fields are no longer written. Never throws — returns empty maps on failure.
   * @returns {Promise<{ accommodationPlaceId: Map<string,string>, documentAssocCount: Map<string,number> }>}
   */
  async getRegistryDisplayIndex(userId, tripId) {
    const index = {
      accommodationPlaceId: new Map(),
      documentAssocCount: new Map()
    };
    try {
      const links = await this.getAllLinks(userId, tripId);
      (links || []).forEach((link) => {
        if ((link.linkLifecycle ?? 'active') !== 'active') return;
        const endpoints = [link.entityLow, link.entityHigh];
        if (link.linkType === 'located_in') {
          const acc = endpoints.find((e) => e?.type === 'accommodation');
          const place = endpoints.find((e) => e?.type === 'place');
          if (acc?.id && place?.id && !index.accommodationPlaceId.has(acc.id)) {
            index.accommodationPlaceId.set(acc.id, place.id);
          }
        } else if (link.linkType === 'document_for') {
          const doc = endpoints.find((e) => e?.type === 'document');
          if (doc?.id) {
            index.documentAssocCount.set(doc.id, (index.documentAssocCount.get(doc.id) || 0) + 1);
          }
        }
      });
    } catch (error) {
      console.warn('connectionRegistryService.getRegistryDisplayIndex failed:', error);
    }
    return index;
  },

  /**
   * Import link documents after entity id remap (e.g. trip import).
   * Remaps endpoint ids via idMap and recomputes dedupKey for each link.
   *
   * @param {string} userId
   * @param {string} tripId
   * @param {Array<Object>} links
   * @param {Record<string, string>} idMap
   * @returns {Promise<{ imported: number, skipped: number }>}
   */
  async importLinkDocuments(userId, tripId, links, idMap) {
    if (!links || links.length === 0) {
      return { imported: 0, skipped: 0 };
    }

    let imported = 0;
    let skipped = 0;
    let batch = writeBatch(db);
    let batchCount = 0;

    for (const link of links) {
      const lowType = link.entityLow?.type;
      const lowOldId = link.entityLow?.id;
      const highType = link.entityHigh?.type;
      const highOldId = link.entityHigh?.id;

      const lowNewId = idMap[lowOldId];
      const highNewId = idMap[highOldId];

      if (!lowType || !highType || !lowNewId || !highNewId) {
        skipped += 1;
        continue;
      }

      const endpointLow = { type: lowType, id: lowNewId };
      const endpointHigh = { type: highType, id: highNewId };
      const newLinkId = doc(linksCollectionRef(userId, tripId)).id;
      const linkDoc = buildLinkDocument(newLinkId, endpointLow, endpointHigh, {
        linkType: link.linkType,
        linkIntent: link.linkIntent,
        displayMode: link.displayMode,
        displayContext: link.displayContext,
        displayTemplate: link.displayTemplate,
        sortOrder: link.sortOrder,
        linkLifecycle: link.linkLifecycle || 'active',
        metadata: link.metadata,
        createdAt: link.createdAt,
        updatedAt: link.updatedAt
      });

      batch.set(linkDocRef(userId, tripId, newLinkId), linkDoc);
      batchCount += 1;
      imported += 1;

      if (batchCount >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    return { imported, skipped };
  },

  async removeAllLinksForEntity(userId, tripId, entityType, entityId, { hardDelete = false } = {}) {
    const links = await this.getLinksForEntity(userId, tripId, entityType, entityId, {
      includeArchived: true
    });
    const activeLinks = links.filter(isActiveLink);
    const removedIds = [];

    for (const link of activeLinks) {
      const linkId = link.linkId || link.id;
      await this.removeLink(userId, tripId, linkId, { hardDelete });
      removedIds.push(linkId);
    }

    if (removedIds.length > 0) {
      eventBridgeService.publish(EVENT_TYPES.ENTITY_LINKS_PURGED, {
        userId,
        tripId,
        entityType,
        entityId,
        linkIds: removedIds,
        timestamp: new Date().toISOString()
      });
    }

    return { entityType, entityId, linkIds: removedIds };
  },

  async getLinkRegistryConfig(userId, tripId) {
    return linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
  },

  async setLinkRegistryConfig(userId, tripId, partialConfig) {
    return linkRegistryConfigService.setLinkRegistryConfig(userId, tripId, partialConfig);
  },

  /**
   * Classifies legacy scan results: missing registry rows vs legacy shadows (registry already has link).
   */
  async classifyLegacyCandidates(userId, tripId) {
    const { linkExtractorService } = await import('./linkExtractorService');
    const scan = await linkExtractorService.scanLegacyCandidates(userId, tripId);
    const missingInRegistry = [];
    const legacyShadows = [];

    for (const candidate of scan.toCreate) {
      const exists = await this.linkExists(
        userId,
        tripId,
        candidate.endpointA,
        candidate.endpointB,
        candidate.linkType
      );
      if (exists) {
        legacyShadows.push(candidate);
      } else {
        missingInRegistry.push(candidate);
      }
    }

    return {
      missingInRegistry,
      legacyShadows,
      unmapped: scan.unmapped
    };
  },

  /**
   * Registry-only / dual-write parity repair:
   * - Strip legacy shadows when the registry already has the link
   * - Create registry rows for legacy-only links (skip legacy write in registry_only)
   */
  async reconcileLegacyParity(userId, tripId, { dryRun = false } = {}) {
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    const classified = await this.classifyLegacyCandidates(userId, tripId);
    const stripped = [];
    const created = [];
    const errors = [];

    for (const candidate of classified.legacyShadows) {
      if (dryRun) {
        stripped.push(candidate);
        continue;
      }
      try {
        const [entityLow, entityHigh] = canonicalize(candidate.endpointA, candidate.endpointB);
        await legacyLinkAdapter.stripLegacyShadow(userId, tripId, {
          entityLow,
          entityHigh,
          linkType: candidate.linkType,
          metadata: candidate.metadata || {}
        });
        stripped.push(candidate);
      } catch (error) {
        errors.push({ candidate, error: error.message });
      }
    }

    for (const candidate of classified.missingInRegistry) {
      if (dryRun) {
        created.push(candidate);
        continue;
      }
      try {
        const result = await this.createLink(
          userId,
          tripId,
          candidate.endpointA,
          candidate.endpointB,
          {
            linkType: candidate.linkType,
            linkIntent: candidate.presentation.linkIntent,
            displayMode: candidate.presentation.displayMode,
            displayContext: candidate.presentation.displayContext,
            metadata: candidate.metadata,
            skipLegacyWrite: config.writeMode === 'registry_only'
          }
        );
        created.push({ ...candidate, linkId: result.linkId });
      } catch (error) {
        errors.push({ candidate, error: error.message });
      }
    }

    const dedup = await this.dedupeAssociatedWithShadows(userId, tripId, { dryRun });

    return {
      dryRun,
      missingInRegistry: classified.missingInRegistry.length,
      legacyShadows: classified.legacyShadows.length,
      stripped: stripped.length,
      created: created.length,
      unmapped: classified.unmapped.length,
      duplicateTypedLinksArchived: dedup.archived,
      errors: [...errors, ...dedup.errors]
    };
  },

  /**
   * Finds active associated_with rows for a pair that also carries a typed link
   * (located_in/stays_on/document_for/departs_from/arrives_at) — a leftover from
   * extraction or manual "Link Entities" linking before the pair had a typed link
   * (Issue 19). dedupKey is per-linkType by design, so both survive independently.
   */
  async findDuplicateTypedLinkPairs(userId, tripId) {
    const links = await this.getAllLinks(userId, tripId);
    const active = links.filter(isActiveLink);
    const byPair = new Map();
    active.forEach((link) => {
      const key = [link.entityLow?.id, link.entityHigh?.id].sort().join('|');
      if (!byPair.has(key)) {
        byPair.set(key, []);
      }
      byPair.get(key).push(link);
    });

    const duplicates = [];
    byPair.forEach((pairLinks) => {
      const typedLinks = pairLinks.filter((link) => link.linkType !== LINK_TYPES.ASSOCIATED_WITH);
      const associatedWithLinks = pairLinks.filter((link) => link.linkType === LINK_TYPES.ASSOCIATED_WITH);
      if (typedLinks.length > 0 && associatedWithLinks.length > 0) {
        duplicates.push({ typedLinks, associatedWithLinks });
      }
    });

    return duplicates;
  },

  /**
   * Archives the associated_with row(s) for any pair that also carries a typed link
   * (Issue 19). Leaves the typed link (stays_on/located_in/etc.) untouched — that one
   * is correct and is what unlink/list/lookup helpers already read.
   */
  async dedupeAssociatedWithShadows(userId, tripId, { dryRun = false } = {}) {
    const duplicates = await this.findDuplicateTypedLinkPairs(userId, tripId);
    const archived = [];
    const errors = [];

    for (const { associatedWithLinks } of duplicates) {
      for (const link of associatedWithLinks) {
        if (dryRun) {
          archived.push(link);
          continue;
        }
        try {
          await this.removeLink(userId, tripId, link.linkId || link.id);
          archived.push(link);
        } catch (error) {
          errors.push({ link, error: error.message });
        }
      }
    }

    return { dryRun, duplicatePairs: duplicates.length, archived: archived.length, errors };
  },

  async compareRegistryToLegacy(userId, tripId) {
    const classified = await this.classifyLegacyCandidates(userId, tripId);
    const registryLinks = await getDocs(linksCollectionRef(userId, tripId));
    return {
      registryCount: registryLinks.size,
      legacyCandidates: classified.missingInRegistry.length,
      legacyShadows: classified.legacyShadows.length,
      duplicates: classified.legacyShadows.length,
      unmapped: classified.unmapped.length
    };
  },

  /**
   * Backfill participantIds on existing link documents (Phase 4 prerequisite).
   * Required before registry_only reads — getLinksForEntity uses array-contains on participantIds.
   *
   * @returns {Promise<{ total: number, updated: number, skipped: number, dryRun: boolean }>}
   */
  async backfillParticipantIds(userId, tripId, { dryRun = false } = {}) {
    const snapshot = await getDocs(linksCollectionRef(userId, tripId));
    let updated = 0;
    let skipped = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const entityLow = data.entityLow;
      const entityHigh = data.entityHigh;

      if (!entityLow?.id || !entityHigh?.id) {
        skipped += 1;
        continue;
      }

      const expected = buildParticipantIds(entityLow, entityHigh);
      const current = Array.isArray(data.participantIds) ? [...data.participantIds].sort() : null;
      const alreadyCorrect = current
        && current.length === expected.length
        && current.every((id, index) => id === expected[index]);

      if (alreadyCorrect) {
        skipped += 1;
        continue;
      }

      if (!dryRun) {
        await updateDoc(linkDocRef(userId, tripId, docSnap.id), {
          participantIds: expected,
          updatedAt: new Date().toISOString()
        });
      }
      updated += 1;
    }

    return {
      total: snapshot.size,
      updated,
      skipped,
      dryRun
    };
  }
};

export default connectionRegistryService;
